import * as THREE from 'three';

/**
 * 户型线稿数据
 * 模拟真实 CAD 建筑平面图
 * 单位：米 (m)
 */

// 房间类型
export interface RoomSegment {
  id: string;
  type: 'wall' | 'door' | 'window';
  room: string;
  points: THREE.Vector2[];
  thickness?: number;
  color?: string;
}

export interface FurniturePiece {
  id: string;
  name: string;
  nameCn: string;
  points: THREE.Vector2[];
  type: 'sofa' | 'bed' | 'table' | 'chair' | 'wardrobe' | 'toilet' | 'sink' | 'shower' | 'bathtub' | 'desk' | 'cabinet' | 'stove' | 'fridge' | 'tv' | 'rug' | 'plant';
  rotation?: number;
}

export interface RoomBlueprint {
  id: string;
  name: string;
  nameCn: string;
  center: THREE.Vector3;
  furniture: FurniturePiece[];
}

/**
 * 户型设计方案
 * 120平方米三室两厅
 */
export function createFloorPlan(): {
  walls: RoomSegment[];
  doors: RoomSegment[];
  windows: RoomSegment[];
  rooms: RoomBlueprint[];
} {
  // 外墙 - 单线表示（CAD风格）
  const walls: RoomSegment[] = [
    // 外墙轮廓 - 15m x 10m
    { id: 'wall-s', type: 'wall', room: 'exterior', points: [new THREE.Vector2(-7.5, -5), new THREE.Vector2(7.5, -5)], thickness: 0.3 },
    { id: 'wall-e', type: 'wall', room: 'exterior', points: [new THREE.Vector2(7.5, -5), new THREE.Vector2(7.5, 5)], thickness: 0.3 },
    { id: 'wall-n', type: 'wall', room: 'exterior', points: [new THREE.Vector2(7.5, 5), new THREE.Vector2(-7.5, 5)], thickness: 0.3 },
    { id: 'wall-w', type: 'wall', room: 'exterior', points: [new THREE.Vector2(-7.5, 5), new THREE.Vector2(-7.5, -5)], thickness: 0.3 },

    // 客厅与走廊隔墙
    { id: 'wall-living-corridor', type: 'wall', room: 'living', points: [new THREE.Vector2(-5, -5), new THREE.Vector2(-5, -0.5)], thickness: 0.2 },
    // 走廊与卫生间隔墙
    { id: 'wall-corridor-bath', type: 'wall', room: 'corridor', points: [new THREE.Vector2(-5, -0.5), new THREE.Vector2(-5, 1.5)], thickness: 0.2 },
    // 走廊与主卧隔墙
    { id: 'wall-corridor-mbr', type: 'wall', room: 'corridor', points: [new THREE.Vector2(-5, 1.5), new THREE.Vector2(-5, 5)], thickness: 0.2 },

    // 客厅与厨房隔墙（半开放）
    { id: 'wall-living-kitchen', type: 'wall', room: 'living', points: [new THREE.Vector2(2, -5), new THREE.Vector2(2, -3.5)], thickness: 0.2 },
    // 厨房与走廊隔墙
    { id: 'wall-kitchen-corridor', type: 'wall', room: 'kitchen', points: [new THREE.Vector2(2, -3.5), new THREE.Vector2(2, -0.5)], thickness: 0.2 },

    // 主卧内部隔墙
    { id: 'wall-mbr1', type: 'wall', room: 'master-bedroom', points: [new THREE.Vector2(-5, 2), new THREE.Vector2(2, 2)], thickness: 0.2 },

    // 卫生间四面墙
    { id: 'wall-bath1', type: 'wall', room: 'bathroom', points: [new THREE.Vector2(-5, -0.5), new THREE.Vector2(-1.5, -0.5)], thickness: 0.2 },
    { id: 'wall-bath2', type: 'wall', room: 'bathroom', points: [new THREE.Vector2(-1.5, -0.5), new THREE.Vector2(-1.5, 1.5)], thickness: 0.2 },
    { id: 'wall-bath3', type: 'wall', room: 'bathroom', points: [new THREE.Vector2(-1.5, 1.5), new THREE.Vector2(2, 1.5)], thickness: 0.2 },
    { id: 'wall-bath4', type: 'wall', room: 'bathroom', points: [new THREE.Vector2(2, 1.5), new THREE.Vector2(2, 2)], thickness: 0.2 },

    // 次卧内部隔墙
    { id: 'wall-br2', type: 'wall', room: 'bedroom-2', points: [new THREE.Vector2(2, -0.5), new THREE.Vector2(2, 2)], thickness: 0.2 },
  ];

  // 门
  const doors: RoomSegment[] = [
    { id: 'door-entrance', type: 'door', room: 'corridor', points: [new THREE.Vector2(-6.5, -5), new THREE.Vector2(-5.5, -5)] },
    { id: 'door-living', type: 'door', room: 'living', points: [new THREE.Vector2(-5, -4), new THREE.Vector2(-5, -2.5)] },
    { id: 'door-kitchen', type: 'door', room: 'kitchen', points: [new THREE.Vector2(2, -4.5), new THREE.Vector2(2, -3.5)] },
    { id: 'door-mbr', type: 'door', room: 'master-bedroom', points: [new THREE.Vector2(-5, 3), new THREE.Vector2(-5, 4)] },
    { id: 'door-br2', type: 'door', room: 'bedroom-2', points: [new THREE.Vector2(3.5, 2), new THREE.Vector2(5.5, 2)] },
    { id: 'door-bath', type: 'door', room: 'bathroom', points: [new THREE.Vector2(-3.5, -0.5), new THREE.Vector2(-2.5, -0.5)] },
  ];

  // 窗
  const windows: RoomSegment[] = [
    { id: 'win-living1', type: 'window', room: 'living', points: [new THREE.Vector2(-4.5, -5), new THREE.Vector2(-1.5, -5)] },
    { id: 'win-living2', type: 'window', room: 'living', points: [new THREE.Vector2(4.5, -5), new THREE.Vector2(7, -5)] },
    { id: 'win-mbr', type: 'window', room: 'master-bedroom', points: [new THREE.Vector2(-4, 5), new THREE.Vector2(0.5, 5)] },
    { id: 'win-br2', type: 'window', room: 'bedroom-2', points: [new THREE.Vector2(3, 5), new THREE.Vector2(7, 5)] },
    { id: 'win-kitchen', type: 'window', room: 'kitchen', points: [new THREE.Vector2(5, -5), new THREE.Vector2(7, -5)] },
  ];

  // 房间和家具
  const rooms: RoomBlueprint[] = [
    {
      id: 'living',
      name: 'Living Room',
      nameCn: '客厅',
      center: new THREE.Vector3(-3.25, 0, -3.25),
      furniture: [
        { id: 'sofa', name: 'Sofa', nameCn: '沙发', points: [new THREE.Vector2(-5, -4.2), new THREE.Vector2(-3, -3)], type: 'sofa' },
        { id: 'coffee-table', name: 'Coffee Table', nameCn: '茶几', points: [new THREE.Vector2(-4.2, -3.8), new THREE.Vector2(-3.5, -3.2)], type: 'table' },
        { id: 'tv', name: 'TV', nameCn: '电视', points: [new THREE.Vector2(-2, -4.6), new THREE.Vector2(-1, -4.4)], type: 'tv' },
        { id: 'tv-cabinet', name: 'TV Cabinet', nameCn: '电视柜', points: [new THREE.Vector2(-2.5, -4.7), new THREE.Vector2(-0.5, -4.4)], type: 'cabinet' },
        { id: 'rug-living', name: 'Rug', nameCn: '地毯', points: [new THREE.Vector2(-5.5, -2.5), new THREE.Vector2(-2.5, -4.8)], type: 'rug' },
        { id: 'plant-1', name: 'Plant', nameCn: '绿植', points: [new THREE.Vector2(-0.5, -2), new THREE.Vector2(0, 0)], type: 'plant' },
        { id: 'plant-2', name: 'Plant', nameCn: '绿植', points: [new THREE.Vector2(-6.5, -2.5), new THREE.Vector2(-6, -2)], type: 'plant' },
      ]
    },
    {
      id: 'kitchen',
      name: 'Kitchen',
      nameCn: '厨房',
      center: new THREE.Vector3(5, 0, -3.75),
      furniture: [
        { id: 'kitchen-counter', name: 'Counter', nameCn: '操作台', points: [new THREE.Vector2(3, -4.6), new THREE.Vector2(6.5, -4.3)], type: 'cabinet' },
        { id: 'kitchen-island', name: 'Island', nameCn: '岛台', points: [new THREE.Vector2(4.5, -3.8), new THREE.Vector2(6, -3.2)], type: 'table' },
        { id: 'fridge', name: 'Fridge', nameCn: '冰箱', points: [new THREE.Vector2(7, -4.2), new THREE.Vector2(7.3, -3)], type: 'fridge' },
        { id: 'stove', name: 'Stove', nameCn: '灶台', points: [new THREE.Vector2(5.5, -4.5), new THREE.Vector2(6.2, -4.2)], type: 'stove' },
      ]
    },
    {
      id: 'master-bedroom',
      name: 'Master Bedroom',
      nameCn: '主卧',
      center: new THREE.Vector3(-2, 0, 3.5),
      furniture: [
        { id: 'bed-mbr', name: 'Bed', nameCn: '双人床', points: [new THREE.Vector2(-4.5, 2.5), new THREE.Vector2(-2, 4.5)], type: 'bed' },
        { id: 'nightstand-1', name: 'Nightstand', nameCn: '床头柜', points: [new THREE.Vector2(-4.7, 3.3), new THREE.Vector2(-4.3, 3.7)], type: 'table' },
        { id: 'nightstand-2', name: 'Nightstand', nameCn: '床头柜', points: [new THREE.Vector2(-2.2, 3.3), new THREE.Vector2(-1.8, 3.7)], type: 'table' },
        { id: 'wardrobe-mbr', name: 'Wardrobe', nameCn: '衣柜', points: [new THREE.Vector2(0.5, 4.5), new THREE.Vector2(2, 4.8)], type: 'wardrobe' },
        { id: 'desk-mbr', name: 'Desk', nameCn: '书桌', points: [new THREE.Vector2(-4.7, 2.8), new THREE.Vector2(-4.2, 2.2)], type: 'desk' },
      ]
    },
    {
      id: 'bedroom-2',
      name: 'Bedroom 2',
      nameCn: '次卧',
      center: new THREE.Vector3(5, 0, 3.5),
      furniture: [
        { id: 'bed-br2', name: 'Bed', nameCn: '单人床', points: [new THREE.Vector2(3.5, 2.5), new THREE.Vector2(5.5, 4.5)], type: 'bed' },
        { id: 'wardrobe-br2', name: 'Wardrobe', nameCn: '衣柜', points: [new THREE.Vector2(6, 4.5), new THREE.Vector2(7.2, 4.8)], type: 'wardrobe' },
        { id: 'desk-br2', name: 'Desk', nameCn: '书桌', points: [new THREE.Vector2(6.5, 3.2), new THREE.Vector2(7.3, 2.8)], type: 'desk' },
      ]
    },
    {
      id: 'bathroom',
      name: 'Bathroom',
      nameCn: '卫生间',
      center: new THREE.Vector3(-3, 0, 0.5),
      furniture: [
        { id: 'toilet', name: 'Toilet', nameCn: '马桶', points: [new THREE.Vector2(-4.5, 1), new THREE.Vector2(-4, 1.4)], type: 'toilet' },
        { id: 'sink', name: 'Sink', nameCn: '洗手台', points: [new THREE.Vector2(-1.8, 1), new THREE.Vector2(-1.5, 1.3)], type: 'sink' },
        { id: 'shower', name: 'Shower', nameCn: '淋浴间', points: [new THREE.Vector2(-4.7, -0.3), new THREE.Vector2(-3, 0.5)], type: 'shower' },
      ]
    },
    {
      id: 'corridor',
      name: 'Corridor',
      nameCn: '走廊',
      center: new THREE.Vector3(-2, 0, 0.5),
      furniture: []
    },
  ];

  return { walls, doors, windows, rooms };
}

/**
 * 创建线稿几何体
 */
export function createBlueprintLines(): {
  wallLines: THREE.Group;
  doorLines: THREE.Group;
  windowLines: THREE.Group;
  furnitureLines: THREE.Group;
  allLines: THREE.Group;
  labels: { text: string; position: THREE.Vector3; room: string }[];
} {
  const floorPlan = createFloorPlan();

  const wallLines = new THREE.Group();
  wallLines.name = 'walls';

  const doorLines = new THREE.Group();
  doorLines.name = 'doors';

  const windowLines = new THREE.Group();
  windowLines.name = 'windows';

  const furnitureLines = new THREE.Group();
  furnitureLines.name = 'furniture';

  const allLines = new THREE.Group();
  allLines.name = 'allLines';

  const labels: { text: string; position: THREE.Vector3; room: string }[] = [];

  // 材质 - CAD蓝图风格
  const wallMaterial = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0
  });

  const doorMaterial = new THREE.LineBasicMaterial({
    color: 0xcccccc,
    transparent: true,
    opacity: 0
  });

  const windowMaterial = new THREE.LineBasicMaterial({
    color: 0x888888,
    transparent: true,
    opacity: 0
  });

  const furnitureMaterial = new THREE.LineBasicMaterial({
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0
  });

  // 生成墙体线 - 干净的单线
  floorPlan.walls.forEach((segment) => {
    const points = segment.points.map(p => new THREE.Vector3(p.x, 0, p.y));
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, wallMaterial.clone());
    line.userData = {
      segmentId: segment.id,
      room: segment.room,
      type: 'wall',
      originalPositions: points.map(p => ({ x: p.x, y: p.y, z: p.z }))
    };
    wallLines.add(line);
    allLines.add(line.clone());
  });

  // 生成门线 - 直线表示
  floorPlan.doors.forEach((segment) => {
    // 门框线 - 直线
    const points = segment.points.map(p => new THREE.Vector3(p.x, 0, p.y));
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, doorMaterial.clone());
    line.userData = {
      segmentId: segment.id,
      room: segment.room,
      type: 'door',
      originalPositions: points.map(p => ({ x: p.x, y: p.y, z: p.z }))
    };
    doorLines.add(line);
    allLines.add(line.clone());
  });

  // 生成窗线 - 三线表示
  floorPlan.windows.forEach((segment) => {
    const p1 = segment.points[0];
    const p2 = segment.points[1];
    const len = p1.distanceTo(p2);

    // 窗户外线
    const outerOffset = 0.08;
    const dx = (p2.x - p1.x) / len;
    const dy = (p2.y - p1.y) / len;
    const nx = -dy * outerOffset;
    const ny = dx * outerOffset;

    const outer1 = new THREE.Vector3(p1.x + nx, 0, p1.y + ny);
    const outer2 = new THREE.Vector3(p2.x + nx, 0, p2.y + ny);
    const inner1 = new THREE.Vector3(p1.x - nx, 0, p1.y - ny);
    const inner2 = new THREE.Vector3(p2.x - nx, 0, p2.y - ny);

    // 外框线
    const outerPoints = [outer1, outer2];
    const outerGeom = new THREE.BufferGeometry().setFromPoints(outerPoints);
    const outerLine = new THREE.Line(outerGeom, windowMaterial.clone());
    outerLine.userData = {
      segmentId: segment.id + '-outer',
      room: segment.room,
      type: 'window',
      originalPositions: outerPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
    };
    windowLines.add(outerLine);
    allLines.add(outerLine.clone());

    // 内框线
    const innerPoints = [inner1, inner2];
    const innerGeom = new THREE.BufferGeometry().setFromPoints(innerPoints);
    const innerLine = new THREE.Line(innerGeom, windowMaterial.clone());
    innerLine.userData = {
      segmentId: segment.id + '-inner',
      room: segment.room,
      type: 'window',
      originalPositions: innerPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
    };
    windowLines.add(innerLine);
    allLines.add(innerLine.clone());

    // 中线（虚线效果用点表示）
    const midPoints = segment.points.map(p => new THREE.Vector3(p.x, 0, p.y));
    const midGeom = new THREE.BufferGeometry().setFromPoints(midPoints);
    const midLine = new THREE.Line(midGeom, windowMaterial.clone());
    midLine.userData = {
      segmentId: segment.id + '-mid',
      room: segment.room,
      type: 'window',
      originalPositions: midPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
    };
    windowLines.add(midLine);
    allLines.add(midLine.clone());
  });

  // 生成家具轮廓 - 精细版本
  floorPlan.rooms.forEach((room) => {
    room.furniture.forEach((item) => {
      const p1 = item.points[0];
      const p2 = item.points[1];

      // 家具矩形轮廓
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;

      const rectPoints = [
        new THREE.Vector3(p1.x, 0, p1.y),
        new THREE.Vector3(p1.x + dx, 0, p1.y),
        new THREE.Vector3(p1.x + dx, 0, p1.y + dy),
        new THREE.Vector3(p1.x, 0, p1.y + dy),
        new THREE.Vector3(p1.x, 0, p1.y), // 闭合
      ];

      const geometry = new THREE.BufferGeometry().setFromPoints(rectPoints);
      const line = new THREE.Line(geometry, furnitureMaterial.clone());
      line.userData = {
        furnitureId: item.id,
        room: room.id,
        type: 'furniture',
        furnitureType: item.type,
        originalPositions: rectPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
      };
      furnitureLines.add(line);
      allLines.add(line.clone());

      // 根据家具类型添加细节线
      switch (item.type) {
        case 'bed':
          // 床头板
          furnitureLines.add(createDetailLine(
            p1.x, p1.y + dy * 0.95,
            p1.x + dx, p1.y + dy * 0.95,
            furnitureMaterial.clone(), item.id + '-headboard', room.id
          ));
          // 枕头分隔线
          furnitureLines.add(createDetailLine(
            p1.x, p1.y + dy * 0.25,
            p1.x + dx, p1.y + dy * 0.25,
            furnitureMaterial.clone(), item.id + '-pillow', room.id
          ));
          break;

        case 'sofa':
          // 左侧扶手
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.08, p1.y,
            p1.x + dx * 0.08, p1.y + dy,
            furnitureMaterial.clone(), item.id + '-arm1', room.id
          ));
          // 右侧扶手
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.92, p1.y,
            p1.x + dx * 0.92, p1.y + dy,
            furnitureMaterial.clone(), item.id + '-arm2', room.id
          ));
          // 座垫分隔
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.33, p1.y,
            p1.x + dx * 0.33, p1.y + dy * 0.85,
            furnitureMaterial.clone(), item.id + '-seat1', room.id
          ));
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.66, p1.y,
            p1.x + dx * 0.66, p1.y + dy * 0.85,
            furnitureMaterial.clone(), item.id + '-seat2', room.id
          ));
          break;

        case 'wardrobe':
          // 门缝
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.5, p1.y,
            p1.x + dx * 0.5, p1.y + dy,
            furnitureMaterial.clone(), item.id + '-divider', room.id
          ));
          // 把手
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.45, p1.y + dy * 0.5,
            p1.x + dx * 0.45, p1.y + dy * 0.5,
            furnitureMaterial.clone(), item.id + '-handle1', room.id
          ));
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.55, p1.y + dy * 0.5,
            p1.x + dx * 0.55, p1.y + dy * 0.5,
            furnitureMaterial.clone(), item.id + '-handle2', room.id
          ));
          break;

        case 'table':
        case 'desk':
          // 桌腿
          const legOffset = 0.08;
          furnitureLines.add(createDetailLine(
            p1.x + dx * legOffset, p1.y + dy * legOffset,
            p1.x + dx * legOffset, p1.y + dy * legOffset,
            furnitureMaterial.clone(), item.id + '-leg1', room.id
          ));
          furnitureLines.add(createDetailLine(
            p1.x + dx * (1 - legOffset), p1.y + dy * legOffset,
            p1.x + dx * (1 - legOffset), p1.y + dy * legOffset,
            furnitureMaterial.clone(), item.id + '-leg2', room.id
          ));
          furnitureLines.add(createDetailLine(
            p1.x + dx * legOffset, p1.y + dy * (1 - legOffset),
            p1.x + dx * legOffset, p1.y + dy * (1 - legOffset),
            furnitureMaterial.clone(), item.id + '-leg3', room.id
          ));
          furnitureLines.add(createDetailLine(
            p1.x + dx * (1 - legOffset), p1.y + dy * (1 - legOffset),
            p1.x + dx * (1 - legOffset), p1.y + dy * (1 - legOffset),
            furnitureMaterial.clone(), item.id + '-leg4', room.id
          ));
          break;

        case 'tv':
          // 电视边框
          const tvMargin = 0.1;
          furnitureLines.add(createDetailLine(
            p1.x + dx * tvMargin, p1.y + dy * tvMargin,
            p1.x + dx * (1 - tvMargin), p1.y + dy * tvMargin,
            furnitureMaterial.clone(), item.id + '-top', room.id
          ));
          furnitureLines.add(createDetailLine(
            p1.x + dx * tvMargin, p1.y + dy * (1 - tvMargin),
            p1.x + dx * (1 - tvMargin), p1.y + dy * (1 - tvMargin),
            furnitureMaterial.clone(), item.id + '-bottom', room.id
          ));
          break;

        case 'toilet':
          // 马桶主体圆形（简化为方形内的圆弧）
          const toiletCx = p1.x + dx * 0.5;
          const toiletCz = p1.y + dy * 0.5;
          const toiletR = Math.min(dx, dy) * 0.4;
          const arcPoints = [];
          for (let i = 0; i <= 12; i++) {
            const angle = (i / 12) * Math.PI;
            arcPoints.push(new THREE.Vector3(
              toiletCx + Math.cos(angle) * toiletR,
              0,
              toiletCz + Math.sin(angle) * toiletR
            ));
          }
          const arcGeom = new THREE.BufferGeometry().setFromPoints(arcPoints);
          const arcLine = new THREE.Line(arcGeom, furnitureMaterial.clone());
          arcLine.userData = {
            furnitureId: item.id + '-arc',
            room: room.id,
            type: 'furniture',
            furnitureType: item.type,
            originalPositions: arcPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
          };
          furnitureLines.add(arcLine);
          break;

        case 'sink':
          // 洗手台圆形
          const sinkCx = p1.x + dx * 0.5;
          const sinkCz = p1.y + dy * 0.5;
          const sinkR = Math.min(dx, dy) * 0.35;
          const sinkArc = [];
          for (let i = 0; i <= 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            sinkArc.push(new THREE.Vector3(
              sinkCx + Math.cos(angle) * sinkR,
              0,
              sinkCz + Math.sin(angle) * sinkR
            ));
          }
          const sinkGeom = new THREE.BufferGeometry().setFromPoints(sinkArc);
          const sinkLine = new THREE.Line(sinkGeom, furnitureMaterial.clone());
          sinkLine.userData = {
            furnitureId: item.id + '-circle',
            room: room.id,
            type: 'furniture',
            furnitureType: item.type,
            originalPositions: sinkArc.map(p => ({ x: p.x, y: p.y, z: p.z }))
          };
          furnitureLines.add(sinkLine);
          break;

        case 'shower':
          // 淋浴间菱形或方形
          const showerCx = p1.x + dx * 0.5;
          const showerCz = p1.y + dy * 0.5;
          const showerR = Math.min(dx, dy) * 0.35;
          const showerArc = [];
          for (let i = 0; i <= 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            showerArc.push(new THREE.Vector3(
              showerCx + Math.cos(angle) * showerR,
              0,
              showerCz + Math.sin(angle) * showerR
            ));
          }
          const showerGeom = new THREE.BufferGeometry().setFromPoints(showerArc);
          const showerLine = new THREE.Line(showerGeom, furnitureMaterial.clone());
          showerLine.userData = {
            furnitureId: item.id + '-circle',
            room: room.id,
            type: 'furniture',
            furnitureType: item.type,
            originalPositions: showerArc.map(p => ({ x: p.x, y: p.y, z: p.z }))
          };
          furnitureLines.add(showerLine);
          break;

        case 'fridge':
          // 冷藏室和冷冻室分隔
          furnitureLines.add(createDetailLine(
            p1.x, p1.y + dy * 0.65,
            p1.x + dx, p1.y + dy * 0.65,
            furnitureMaterial.clone(), item.id + '-divider', room.id
          ));
          // 门把手
          furnitureLines.add(createDetailLine(
            p1.x + dx * 0.9, p1.y + dy * 0.4,
            p1.x + dx * 0.9, p1.y + dy * 0.4,
            furnitureMaterial.clone(), item.id + '-handle', room.id
          ));
          break;

        case 'stove':
          // 灶眼位置
          const burnerPositions = [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]];
          burnerPositions.forEach(([bx, bz], idx) => {
            const burnerR = 0.08;
            const burnerArc = [];
            for (let i = 0; i <= 8; i++) {
              const angle = (i / 8) * Math.PI * 2;
              burnerArc.push(new THREE.Vector3(
                p1.x + dx * bx + Math.cos(angle) * burnerR,
                0,
                p1.y + dy * bz + Math.sin(angle) * burnerR
              ));
            }
            const burnerGeom = new THREE.BufferGeometry().setFromPoints(burnerArc);
            const burnerLine = new THREE.Line(burnerGeom, furnitureMaterial.clone());
            burnerLine.userData = {
              furnitureId: item.id + '-burner' + idx,
              room: room.id,
              type: 'furniture',
              furnitureType: item.type,
              originalPositions: burnerArc.map(p => ({ x: p.x, y: p.y, z: p.z }))
            };
            furnitureLines.add(burnerLine);
          });
          break;

        case 'rug':
          // 地毯装饰线
          const rugMargin = 0.08;
          const rugInnerPoints = [
            new THREE.Vector3(p1.x + dx * rugMargin, 0, p1.y + dy * rugMargin),
            new THREE.Vector3(p1.x + dx * (1 - rugMargin), 0, p1.y + dy * rugMargin),
            new THREE.Vector3(p1.x + dx * (1 - rugMargin), 0, p1.y + dy * (1 - rugMargin)),
            new THREE.Vector3(p1.x + dx * rugMargin, 0, p1.y + dy * (1 - rugMargin)),
            new THREE.Vector3(p1.x + dx * rugMargin, 0, p1.y + dy * rugMargin),
          ];
          const rugGeom = new THREE.BufferGeometry().setFromPoints(rugInnerPoints);
          const rugLine = new THREE.Line(rugGeom, furnitureMaterial.clone());
          rugLine.userData = {
            furnitureId: item.id + '-inner',
            room: room.id,
            type: 'furniture',
            furnitureType: item.type,
            originalPositions: rugInnerPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
          };
          furnitureLines.add(rugLine);
          break;

        case 'plant':
          // 植物圆形
          const plantCx = p1.x + dx * 0.5;
          const plantCz = p1.y + dy * 0.5;
          const plantR = Math.min(dx, dy) * 0.4;
          const plantArc = [];
          for (let i = 0; i <= 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            plantArc.push(new THREE.Vector3(
              plantCx + Math.cos(angle) * plantR,
              0,
              plantCz + Math.sin(angle) * plantR
            ));
          }
          const plantGeom = new THREE.BufferGeometry().setFromPoints(plantArc);
          const plantLine = new THREE.Line(plantGeom, furnitureMaterial.clone());
          plantLine.userData = {
            furnitureId: item.id + '-circle',
            room: room.id,
            type: 'furniture',
            furnitureType: item.type,
            originalPositions: plantArc.map(p => ({ x: p.x, y: p.y, z: p.z }))
          };
          furnitureLines.add(plantLine);
          break;
      }
    });

    // 房间标签
    labels.push({
      text: room.nameCn,
      position: room.center,
      room: room.id
    });
  });

  return { wallLines, doorLines, windowLines, furnitureLines, allLines, labels };
}

/**
 * 创建细节线辅助函数
 */
function createDetailLine(
  x1: number, z1: number,
  x2: number, z2: number,
  material: THREE.LineBasicMaterial,
  id: string,
  room: string
): THREE.Line {
  const points = [
    new THREE.Vector3(x1, 0, z1),
    new THREE.Vector3(x2, 0, z2)
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(geometry, material);
  line.userData = {
    furnitureId: id,
    room: room,
    type: 'furniture',
    originalPositions: points.map(p => ({ x: p.x, y: p.y, z: p.z }))
  };
  return line;
}

/**
 * 为线稿设置散落状态
 * 墙体从中心散开，家具从外部飞入
 */
export function setupScatteredPositions(linesGroup: THREE.Group): void {
  const rooms = ['living', 'kitchen', 'master-bedroom', 'bedroom-2', 'bathroom', 'corridor', 'exterior'];

  // 散落位置 - 围绕中心分布
  const scatterConfigs: { offset: THREE.Vector3; rotY: number }[] = [
    { offset: new THREE.Vector3(-8, 0, -6), rotY: 0.3 },   // 客厅 - 左前
    { offset: new THREE.Vector3(8, 0, -5), rotY: -0.2 },   // 厨房 - 右前
    { offset: new THREE.Vector3(-9, 0, 5), rotY: 0.1 },    // 主卧 - 左后
    { offset: new THREE.Vector3(9, 0, 4), rotY: -0.3 },     // 次卧 - 右后
    { offset: new THREE.Vector3(-6, 0, -7), rotY: 0.4 },   // 卫生间 - 前
    { offset: new THREE.Vector3(0, 0, -8), rotY: -0.1 },   // 走廊 - 上
    { offset: new THREE.Vector3(0, 0, 8), rotY: 0.2 },     // 外墙 - 下
  ];

  linesGroup.children.forEach((lineObj) => {
    const line = lineObj as THREE.Line;
    const userData = line.userData;

    if (!userData.originalPositions) return;

    // 根据房间分配散落位置
    const room = userData.room || 'exterior';
    const roomIndex = rooms.indexOf(room);
    const scatter = scatterConfigs[roomIndex >= 0 ? roomIndex : rooms.length - 1];

    // 计算原始位置中心
    const positions: Array<{x: number; y: number; z: number}> = userData.originalPositions;
    let centerX = 0, centerZ = 0;
    positions.forEach((p: {x: number; z: number}) => {
      centerX += p.x;
      centerZ += p.z;
    });
    centerX /= positions.length;
    centerZ /= positions.length;

    // 设置散落位置 - 基于原始位置的偏移
    const scatteredPosArray: Array<{x: number; y: number; z: number}> = positions.map((p: {x: number; y: number; z: number}) => ({
      x: p.x + scatter.offset.x + (Math.random() - 0.5) * 3,
      y: p.y + (Math.random() - 0.5) * 4,
      z: p.z + scatter.offset.z + (Math.random() - 0.5) * 3
    }));

    line.userData.scatteredPositions = scatteredPosArray;
    line.userData.scatterOffset = scatter;

    // 初始状态 - 散落
    const posAttr = line.geometry.getAttribute('position');
    scatteredPosArray.forEach((sp: {x: number; y: number; z: number}, i: number) => {
      posAttr.setXYZ(i, sp.x, sp.y, sp.z);
    });
    posAttr.needsUpdate = true;

    // 随机旋转和缩放
    line.rotation.y = scatter.rotY + (Math.random() - 0.5) * 0.4;
    line.scale.setScalar(0.7 + Math.random() * 0.6);

    // 透明度
    const material = line.material as THREE.LineBasicMaterial;
    material.opacity = 0.3 + Math.random() * 0.4;
  });
}

export type { THREE };
